import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Play, Pause, RotateCcw, Trash2, AlertTriangle, Clock, Zap, FileText, FileDown, MoreVertical, ChevronUp, ChevronDown, BrainCircuit, User, Star, Settings, CheckCircle2, XCircle, Activity, Timer, DollarSign, Terminal, Database, AlertCircle, X } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
interface AIQueueManagerProps {
  success: (msg: string) => void;
  setDetailPanel: (panel: any) => void;
}
// --- MOCK DATA ---
const MOCK_QUEUE = Array.from({
  length: 156
}).map((_, i) => {
  const id = `GEN-${8000 + i}`;
  const statusRand = Math.random();
  let status = 'queued';
  if (statusRand > 0.9) status = 'failed';else if (statusRand > 0.8) status = 'stuck';else if (statusRand > 0.7) status = 'retrying';else if (statusRand > 0.5) status = 'processing';else if (statusRand > 0.4) status = 'canceled';else if (statusRand > 0.1) status = 'completed';
  const priorityRand = Math.random();
  const priority = priorityRand > 0.8 ? 'high' : priorityRand > 0.3 ? 'medium' : 'low';
  const typeRand = Math.random();
  const type = typeRand > 0.7 ? 'Натальная карта' : typeRand > 0.4 ? 'Совместимость' : typeRand > 0.2 ? 'Соляр' : 'Прогноз';
  const sourceRand = Math.random();
  const source = sourceRand > 0.8 ? 'astrologer' : sourceRand > 0.1 ? 'user' : 'system';
  const modelRand = Math.random();
  const model = modelRand > 0.6 ? 'GPT-5' : modelRand > 0.3 ? 'Claude 3.5' : 'Llama 3';
  const hasPdf = status === 'completed' && Math.random() > 0.1;
  return {
    id,
    userId: `USR-${1000 + Math.floor(Math.random() * 500)}`,
    userName: ['Анна П.', 'Иван С.', 'Мария К.', 'Олег В.', 'Елена Д.'][Math.floor(Math.random() * 5)],
    astrologerId: source === 'astrologer' ? `AST-${200 + Math.floor(Math.random() * 50)}` : null,
    type,
    priority,
    position: status === 'queued' ? Math.floor(Math.random() * 100) + 1 : null,
    status,
    waitTime: Math.floor(Math.random() * 300) + ' сек',
    eta: status === 'queued' || status === 'processing' ? Math.floor(Math.random() * 120) + ' сек' : null,
    model,
    retries: status === 'retrying' || status === 'failed' ? Math.floor(Math.random() * 3) + 1 : 0,
    cost: (Math.random() * 0.5 + 0.05).toFixed(2),
    hasPdf,
    pdfUrl: hasPdf ? `/reports/${id}.pdf` : null,
    source,
    createdAt: new Date(Date.now() - Math.random() * 10000000).toISOString(),
    errorReason: status === 'failed' || status === 'stuck' ? 'API Timeout exceeded 120s' : null,
    orderId: `ORD-${5000 + i}`,
    tokens: Math.floor(Math.random() * 8000) + 1000,
    userInput: {
      birthDate: ['1990-03-15', '1985-07-22', '1998-11-08', '2001-01-30', '1976-06-12'][Math.floor(Math.random() * 5)],
      birthTime: ['14:30', '08:15', '23:45', '06:00', '17:20'][Math.floor(Math.random() * 5)],
      birthPlace: ['Москва, Россия', 'Санкт-Петербург, Россия', 'Киев, Украина', 'Берлин, Германия', 'Париж, Франция'][Math.floor(Math.random() * 5)],
      partnerBirthDate: type === 'Совместимость' ? ['1992-05-10', '1988-12-01', '1995-09-25'][Math.floor(Math.random() * 3)] : null,
      partnerBirthTime: type === 'Совместимость' ? ['10:00', '19:30', '03:15'][Math.floor(Math.random() * 3)] : null,
      partnerBirthPlace: type === 'Совместимость' ? ['Казань, Россия', 'Минск, Беларусь', 'Варшава, Польша'][Math.floor(Math.random() * 3)] : null,
      language: 'ru',
      style: ['detailed', 'brief', 'poetic'][Math.floor(Math.random() * 3)]
    }
  };
}).sort((a, b) => {
  // Sort by priority (high > medium > low) then position
  const pMap = {
    high: 3,
    medium: 2,
    low: 1
  };
  if (pMap[a.priority as keyof typeof pMap] !== pMap[b.priority as keyof typeof pMap]) {
    return pMap[b.priority as keyof typeof pMap] - pMap[a.priority as keyof typeof pMap];
  }
  return (a.position || 999) - (b.position || 999);
});
const STATUS_CONFIG: Record<string, {
  label: string;
  variant: any;
  color: string;
}> = {
  queued: {
    label: 'В очереди',
    variant: 'warning',
    color: 'text-amber-600'
  },
  processing: {
    label: 'Генерация',
    variant: 'success',
    color: 'text-emerald-600'
  },
  retrying: {
    label: 'Повтор',
    variant: 'warning',
    color: 'text-orange-600'
  },
  failed: {
    label: 'Ошибка',
    variant: 'error',
    color: 'text-rose-600'
  },
  stuck: {
    label: 'Зависло',
    variant: 'error',
    color: 'text-rose-800'
  },
  canceled: {
    label: 'Отменено',
    variant: 'neutral',
    color: 'text-slate-500'
  },
  completed: {
    label: 'Готово',
    variant: 'success',
    color: 'text-emerald-600'
  }
};
const PRIORITY_CONFIG: Record<string, {
  label: string;
  icon: any;
  color: string;
}> = {
  high: {
    label: 'Высокий',
    icon: AlertTriangle,
    color: 'text-rose-500'
  },
  medium: {
    label: 'Средний',
    icon: Activity,
    color: 'text-blue-500'
  },
  low: {
    label: 'Низкий',
    icon: Clock,
    color: 'text-slate-400'
  }
};
export function AIQueueManager({
  success,
  setDetailPanel
}: AIQueueManagerProps) {
  const [queue, setQueue] = useState(MOCK_QUEUE);
  const [isPaused, setIsPaused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    type: 'all',
    source: 'all',
    hasPdf: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTab, setDetailTab] = useState('main');
  // Live updates simulation
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setQueue(prev => {
        const newQueue = [...prev];
        // Randomly process some queued items
        const queuedIdx = newQueue.findIndex(q => q.status === 'queued');
        if (queuedIdx !== -1 && Math.random() > 0.5) {
          newQueue[queuedIdx] = {
            ...newQueue[queuedIdx],
            status: 'processing',
            eta: '45 сек'
          };
        }
        // Randomly complete some processing items
        const procIdx = newQueue.findIndex(q => q.status === 'processing');
        if (procIdx !== -1 && Math.random() > 0.7) {
          newQueue[procIdx] = {
            ...newQueue[procIdx],
            status: 'completed',
            eta: null,
            hasPdf: true,
            pdfUrl: `/reports/${newQueue[procIdx].id}.pdf`
          };
        }
        return newQueue;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused]);
  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // Stats
  const stats = useMemo(() => {
    return {
      total: queue.length,
      processing: queue.filter(q => q.status === 'processing').length,
      queued: queue.filter(q => q.status === 'queued').length,
      highPriority: queue.filter(q => q.priority === 'high' && ['queued', 'processing'].includes(q.status)).length,
      stuck: queue.filter(q => q.status === 'stuck').length,
      failed: queue.filter(q => q.status === 'failed').length,
      avgWait: '42 сек',
      avgGen: '1м 15с'
    };
  }, [queue]);
  // Filtering
  const filteredQueue = useMemo(() => {
    return queue.filter(item => {
      if (searchQuery && !item.id.toLowerCase().includes(searchQuery.toLowerCase()) && !item.userName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filters.status !== 'all' && item.status !== filters.status) return false;
      if (filters.priority !== 'all' && item.priority !== filters.priority) return false;
      if (filters.type !== 'all' && item.type !== filters.type) return false;
      if (filters.source !== 'all' && item.source !== filters.source) return false;
      if (filters.hasPdf !== 'all') {
        if (filters.hasPdf === 'yes' && !item.hasPdf) return false;
        if (filters.hasPdf === 'no' && item.hasPdf) return false;
      }
      return true;
    });
  }, [queue, searchQuery, filters]);
  const paginatedQueue = filteredQueue.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Actions
  const handleGlobalAction = (action: string) => {
    switch (action) {
      case 'pause':
        setIsPaused(true);
        success('Очередь приостановлена');
        break;
      case 'resume':
        setIsPaused(false);
        success('Очередь запущена');
        break;
      case 'retry_failed':
        setQueue(prev => prev.map(q => q.status === 'failed' ? {
          ...q,
          status: 'queued',
          retries: q.retries + 1
        } : q));
        success('Ошибочные задачи отправлены на повтор');
        break;
      case 'clear_stuck':
        setQueue(prev => prev.map(q => q.status === 'stuck' ? {
          ...q,
          status: 'canceled'
        } : q));
        success('Зависшие задачи отменены');
        break;
      case 'refresh':
        success('Данные обновлены');
        break;
    }
  };
  const handleRowAction = (action: string, item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setOpenMenuId(null);
    switch (action) {
      case 'view':
        setSelectedItem(item);
        setDetailTab('main');
        setShowDetailModal(true);
        break;
      case 'pdf':
        success(`Скачивание PDF для ${item.id}...`);
        break;
      case 'priority_up':
        setQueue(prev => prev.map(q => q.id === item.id ? {
          ...q,
          priority: q.priority === 'low' ? 'medium' : 'high'
        } : q));
        success(`Приоритет повышен для ${item.id}`);
        break;
      case 'priority_down':
        setQueue(prev => prev.map(q => q.id === item.id ? {
          ...q,
          priority: q.priority === 'high' ? 'medium' : 'low'
        } : q));
        success(`Приоритет понижен для ${item.id}`);
        break;
      case 'run':
        setQueue(prev => prev.map(q => q.id === item.id ? {
          ...q,
          status: 'processing'
        } : q));
        success(`Генерация ${item.id} запущена принудительно`);
        break;
      case 'retry':
        setQueue(prev => prev.map(q => q.id === item.id ? {
          ...q,
          status: 'queued',
          retries: q.retries + 1
        } : q));
        success(`Генерация ${item.id} отправлена на повтор`);
        break;
      case 'cancel':
        setQueue(prev => prev.map(q => q.id === item.id ? {
          ...q,
          status: 'canceled'
        } : q));
        success(`Генерация ${item.id} отменена`);
        break;
    }
  };
  // Detail Modal Content
  const renderDetailModal = () => {
    if (!selectedItem) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setShowDetailModal(false)} data-id="element-9567">
        <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-9568">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white shrink-0" data-id="element-9569">
            <div data-id="element-9570">
              <div className="flex items-center gap-3 mb-1" data-id="element-9571">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-9572">
                  Отчёт {selectedItem.id}
                </h2>
                <Badge variant={STATUS_CONFIG[selectedItem.status]?.variant} data-id="element-9573">
                  {STATUS_CONFIG[selectedItem.status]?.label}
                </Badge>
                {selectedItem.priority === 'high' && <Badge variant="error" size="sm" data-id="element-9574">
                    High Priority
                  </Badge>}
              </div>
              <p className="text-sm text-slate-500" data-id="element-9575">
                Создан:{' '}
                {new Date(selectedItem.createdAt).toLocaleString('ru-RU')} •
                Заказ: {selectedItem.orderId}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-9576">
              {selectedItem.hasPdf && <Button variant="outline" icon={<FileDown className="w-4 h-4" data-id="element-9578" />} onClick={() => handleRowAction('pdf', selectedItem)} data-id="element-9577">
                  Скачать PDF
                </Button>}
              <button onClick={() => setShowDetailModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors" data-id="element-9579">
                <X className="w-5 h-5" data-id="element-9580" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 border-b border-slate-100 bg-white shrink-0" data-id="element-9581">
            <Tabs activeTab={detailTab} onChange={setDetailTab} tabs={[{
            id: 'main',
            label: 'Основное'
          }, {
            id: 'content',
            label: 'Контент'
          }, {
            id: 'ai',
            label: 'AI Данные'
          }, {
            id: 'logs',
            label: 'Логи'
          }, {
            id: 'errors',
            label: 'Ошибки'
          }]} data-id="element-9582" />
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto bg-slate-50 flex-1" data-id="element-9583">
            {detailTab === 'main' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9584">
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9585">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9586">
                    Информация
                  </h3>
                  <div className="space-y-3" data-id="element-9587">
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9588">
                      <span className="text-sm text-slate-500" data-id="element-9589">
                        Пользователь
                      </span>
                      <span className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-9590">
                        <User className="w-3.5 h-3.5 text-slate-400" data-id="element-9591" />{' '}
                        {selectedItem.userName} ({selectedItem.userId})
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9592">
                      <span className="text-sm text-slate-500" data-id="element-9593">Астролог</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9594">
                        {selectedItem.astrologerId || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9595">
                      <span className="text-sm text-slate-500" data-id="element-9596">Тип отчёта</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9597">
                        {selectedItem.type}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9598">
                      <span className="text-sm text-slate-500" data-id="element-9599">Источник</span>
                      <span className="text-sm font-medium text-slate-900 capitalize" data-id="element-9600">
                        {selectedItem.source}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9601">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9602">
                    Данные клиента
                  </h3>
                  <div className="space-y-3" data-id="element-9603">
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9604">
                      <span className="text-sm text-slate-500" data-id="element-9605">
                        Дата рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9606">
                        {selectedItem.userInput?.birthDate || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9607">
                      <span className="text-sm text-slate-500" data-id="element-9608">
                        Время рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9609">
                        {selectedItem.userInput?.birthTime || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9610">
                      <span className="text-sm text-slate-500" data-id="element-9611">
                        Место рождения
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9612">
                        {selectedItem.userInput?.birthPlace || '—'}
                      </span>
                    </div>
                    {selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate && <>
                          <div className="pt-2 pb-1" data-id="element-9613">
                            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider" data-id="element-9614">
                              Партнёр
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9615">
                            <span className="text-sm text-slate-500" data-id="element-9616">
                              Дата рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9617">
                              {selectedItem.userInput.partnerBirthDate}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9618">
                            <span className="text-sm text-slate-500" data-id="element-9619">
                              Время рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9620">
                              {selectedItem.userInput.partnerBirthTime || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9621">
                            <span className="text-sm text-slate-500" data-id="element-9622">
                              Место рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9623">
                              {selectedItem.userInput.partnerBirthPlace || '—'}
                            </span>
                          </div>
                        </>}
                  </div>
                </Card>
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9624">
                  <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9625">
                    Статус генерации
                  </h3>
                  <div className="space-y-3" data-id="element-9626">
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9627">
                      <span className="text-sm text-slate-500" data-id="element-9628">Модель</span>
                      <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded" data-id="element-9629">
                        {selectedItem.model}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9630">
                      <span className="text-sm text-slate-500" data-id="element-9631">
                        Позиция в очереди
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9632">
                        {selectedItem.position ? `#${selectedItem.position}` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9633">
                      <span className="text-sm text-slate-500" data-id="element-9634">
                        Время ожидания
                      </span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9635">
                        {selectedItem.waitTime}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9636">
                      <span className="text-sm text-slate-500" data-id="element-9637">ETA</span>
                      <span className="text-sm font-medium text-amber-600" data-id="element-9638">
                        {selectedItem.eta || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9639">
                      <span className="text-sm text-slate-500" data-id="element-9640">Попытки</span>
                      <span className="text-sm font-medium text-slate-900" data-id="element-9641">
                        {selectedItem.retries}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>}

            {detailTab === 'content' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9642">
                {selectedItem.status === 'completed' ? <div className="space-y-4" data-id="element-9643">
                    <div className="p-4 bg-slate-100 rounded-lg font-mono text-xs text-slate-700 whitespace-pre-wrap" data-id="element-9644">
                      {`{\n  "title": "${selectedItem.type}",\n  "sections": [\n    {\n      "heading": "Введение",\n      "content": "Ваша натальная карта показывает сильное влияние стихии воды..."\n    }\n  ]\n}`}
                    </div>
                  </div> : <div className="text-center py-12 text-slate-500" data-id="element-9645">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-9646" />
                    <p data-id="element-9647">
                      Контент пока недоступен. Статус:{' '}
                      {STATUS_CONFIG[selectedItem.status]?.label}
                    </p>
                  </div>}
              </Card>}

            {detailTab === 'ai' && <div className="space-y-6" data-id="element-9648">
                {/* User Input Data */}
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9649">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-9650">
                    Данные пользователя (входные)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9651">
                    <div className="space-y-3" data-id="element-9652">
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9653">
                        <span className="text-sm text-slate-500" data-id="element-9654">
                          Дата рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-9655">
                          {selectedItem.userInput?.birthDate || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9656">
                        <span className="text-sm text-slate-500" data-id="element-9657">
                          Время рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-9658">
                          {selectedItem.userInput?.birthTime || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9659">
                        <span className="text-sm text-slate-500" data-id="element-9660">
                          Место рождения
                        </span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-9661">
                          {selectedItem.userInput?.birthPlace || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9662">
                        <span className="text-sm text-slate-500" data-id="element-9663">Язык</span>
                        <span className="text-sm font-medium text-slate-900" data-id="element-9664">
                          {selectedItem.userInput?.language === 'ru' ? 'Русский' : selectedItem.userInput?.language || '—'}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9665">
                        <span className="text-sm text-slate-500" data-id="element-9666">
                          Стиль отчёта
                        </span>
                        <span className="text-sm font-medium text-slate-900 capitalize" data-id="element-9667">
                          {selectedItem.userInput?.style || '—'}
                        </span>
                      </div>
                    </div>
                    {selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate && <div className="space-y-3" data-id="element-9668">
                          <div className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-1" data-id="element-9669">
                            Партнёр
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9670">
                            <span className="text-sm text-slate-500" data-id="element-9671">
                              Дата рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9672">
                              {selectedItem.userInput.partnerBirthDate}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9673">
                            <span className="text-sm text-slate-500" data-id="element-9674">
                              Время рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9675">
                              {selectedItem.userInput.partnerBirthTime || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100" data-id="element-9676">
                            <span className="text-sm text-slate-500" data-id="element-9677">
                              Место рождения
                            </span>
                            <span className="text-sm font-medium text-slate-900" data-id="element-9678">
                              {selectedItem.userInput.partnerBirthPlace || '—'}
                            </span>
                          </div>
                        </div>}
                  </div>
                </Card>

                {/* Generated Prompt */}
                <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9679">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9680">
                    Сгенерированный промпт
                  </h4>
                  <div className="p-4 bg-slate-900 text-emerald-400 rounded-lg font-mono text-xs whitespace-pre-wrap max-h-[250px] overflow-y-auto" data-id="element-9681">
                    {`You are an expert astrologer with 20+ years of experience.
Generate a comprehensive "${selectedItem.type}" report in Russian.

User Data:
- Birth date: ${selectedItem.userInput?.birthDate || 'N/A'}
- Birth time: ${selectedItem.userInput?.birthTime || 'N/A'}
- Birth place: ${selectedItem.userInput?.birthPlace || 'N/A'}${selectedItem.type === 'Совместимость' && selectedItem.userInput?.partnerBirthDate ? `

Partner Data:
- Birth date: ${selectedItem.userInput.partnerBirthDate}
- Birth time: ${selectedItem.userInput.partnerBirthTime || 'N/A'}
- Birth place: ${selectedItem.userInput.partnerBirthPlace || 'N/A'}` : ''}

Style: ${selectedItem.userInput?.style || 'detailed'}
Language: ${selectedItem.userInput?.language || 'ru'}
Model: ${selectedItem.model}
Max tokens: 8192
Temperature: 0.7`}
                  </div>
                </Card>

                {/* Token & Cost Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-9682">
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-9683">
                    <div className="flex items-center gap-2 mb-1" data-id="element-9684">
                      <Database className="w-4 h-4 text-blue-500" data-id="element-9685" />
                      <span className="text-xs text-slate-500" data-id="element-9686">
                        Токены (вход)
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900" data-id="element-9687">
                      {selectedItem.tokens.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-9688">
                    <div className="flex items-center gap-2 mb-1" data-id="element-9689">
                      <Database className="w-4 h-4 text-emerald-500" data-id="element-9690" />
                      <span className="text-xs text-slate-500" data-id="element-9691">
                        Токены (выход)
                      </span>
                    </div>
                    <div className="text-lg font-bold text-slate-900" data-id="element-9692">
                      {Math.floor(selectedItem.tokens * 1.5).toLocaleString()}
                    </div>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-9693">
                    <div className="flex items-center gap-2 mb-1" data-id="element-9694">
                      <DollarSign className="w-4 h-4 text-amber-500" data-id="element-9695" />
                      <span className="text-xs text-slate-500" data-id="element-9696">Стоимость</span>
                    </div>
                    <div className="text-lg font-bold text-emerald-600" data-id="element-9697">
                      ${selectedItem.cost}
                    </div>
                  </div>
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-id="element-9698">
                    <div className="flex items-center gap-2 mb-1" data-id="element-9699">
                      <Timer className="w-4 h-4 text-indigo-500" data-id="element-9700" />
                      <span className="text-xs text-slate-500" data-id="element-9701">Модель</span>
                    </div>
                    <div className="text-lg font-bold text-indigo-600" data-id="element-9702">
                      {selectedItem.model}
                    </div>
                  </div>
                </div>
              </div>}

            {detailTab === 'logs' && <Card padding="none" className="shadow-sm border-slate-200 overflow-hidden" data-id="element-9703">
                <div className="bg-slate-900 text-emerald-400 font-mono text-xs p-4 h-[400px] overflow-y-auto" data-id="element-9704">
                  <div className="mb-2 text-slate-500" data-id="element-9705">
                    [{new Date(selectedItem.createdAt).toISOString()}] [INFO]
                    Job added to queue
                  </div>
                  <div className="mb-2 text-slate-500" data-id="element-9706">
                    [{new Date(selectedItem.createdAt).toISOString()}] [INFO]
                    Priority set to {selectedItem.priority}
                  </div>
                  {['processing', 'completed', 'failed', 'stuck', 'retrying'].includes(selectedItem.status) && <div className="mb-2 text-blue-400" data-id="element-9707">
                      [{new Date(Date.now() - 60000).toISOString()}] [INFO]
                      Worker picked up job. Model: {selectedItem.model}
                    </div>}
                  {selectedItem.status === 'completed' && <>
                      <div className="mb-2 text-emerald-400" data-id="element-9708">
                        [{new Date(Date.now() - 10000).toISOString()}] [SUCCESS]
                        Generation completed. Tokens: {selectedItem.tokens}
                      </div>
                      <div className="mb-2 text-emerald-400" data-id="element-9709">
                        [{new Date(Date.now() - 5000).toISOString()}] [SUCCESS]
                        PDF generated and saved to S3
                      </div>
                    </>}
                  {['failed', 'stuck', 'retrying'].includes(selectedItem.status) && <div className="mb-2 text-rose-400" data-id="element-9710">
                      [{new Date().toISOString()}] [ERROR]{' '}
                      {selectedItem.errorReason}
                    </div>}
                </div>
              </Card>}

            {detailTab === 'errors' && <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9711">
                {selectedItem.errorReason ? <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3" data-id="element-9712">
                    <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-9713" />
                    <div data-id="element-9714">
                      <h4 className="font-bold text-rose-900 mb-1" data-id="element-9715">
                        Ошибка генерации
                      </h4>
                      <p className="text-sm text-rose-700" data-id="element-9716">
                        {selectedItem.errorReason}
                      </p>
                      <div className="mt-4 flex gap-2" data-id="element-9717">
                        <Button variant="primary" size="sm" onClick={() => handleRowAction('retry', selectedItem)} data-id="element-9718">
                          Повторить
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleRowAction('cancel', selectedItem)} data-id="element-9719">
                          Отменить
                        </Button>
                      </div>
                    </div>
                  </div> : <div className="text-center py-12 text-slate-500" data-id="element-9720">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-400" data-id="element-9721" />
                    <p data-id="element-9722">Ошибок не обнаружено</p>
                  </div>}
              </Card>}
          </div>
        </Card>
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-9723">
      {/* 1. SUMMARY STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-9724">
        <Card padding="sm" className="bg-white border-slate-200 shadow-sm flex flex-col justify-center cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => setFilters({
        ...filters,
        status: 'all'
      })} data-id="element-9725">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1" data-id="element-9726">
            Всего
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-9727">{stats.total}</div>
        </Card>
        <Card padding="sm" className="bg-emerald-50 border-emerald-100 shadow-sm flex flex-col justify-center cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => setFilters({
        ...filters,
        status: 'processing'
      })} data-id="element-9728">
          <div className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-1" data-id="element-9729">
            В работе
          </div>
          <div className="text-xl font-bold text-emerald-700" data-id="element-9730">
            {stats.processing}
          </div>
        </Card>
        <Card padding="sm" className="bg-amber-50 border-amber-100 shadow-sm flex flex-col justify-center cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setFilters({
        ...filters,
        status: 'queued'
      })} data-id="element-9731">
          <div className="text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-1" data-id="element-9732">
            В очереди
          </div>
          <div className="text-xl font-bold text-amber-700" data-id="element-9733">{stats.queued}</div>
        </Card>
        <Card padding="sm" className="bg-rose-50 border-rose-100 shadow-sm flex flex-col justify-center cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setFilters({
        ...filters,
        priority: 'high'
      })} data-id="element-9734">
          <div className="text-[10px] text-rose-600 font-bold uppercase tracking-wider mb-1" data-id="element-9735">
            High Priority
          </div>
          <div className="text-xl font-bold text-rose-700" data-id="element-9736">
            {stats.highPriority}
          </div>
        </Card>
        <Card padding="sm" className="bg-rose-100 border-rose-200 shadow-sm flex flex-col justify-center cursor-pointer hover:border-rose-400 transition-colors" onClick={() => setFilters({
        ...filters,
        status: 'stuck'
      })} data-id="element-9737">
          <div className="text-[10px] text-rose-800 font-bold uppercase tracking-wider mb-1" data-id="element-9738">
            Зависло
          </div>
          <div className="text-xl font-bold text-rose-900" data-id="element-9739">{stats.stuck}</div>
        </Card>
        <Card padding="sm" className="bg-orange-50 border-orange-100 shadow-sm flex flex-col justify-center cursor-pointer hover:border-orange-300 transition-colors" onClick={() => setFilters({
        ...filters,
        status: 'failed'
      })} data-id="element-9740">
          <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-1" data-id="element-9741">
            Ошибки
          </div>
          <div className="text-xl font-bold text-orange-700" data-id="element-9742">
            {stats.failed}
          </div>
        </Card>
        <Card padding="sm" className="bg-slate-50 border-slate-200 shadow-sm flex flex-col justify-center" data-id="element-9743">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1" data-id="element-9744">
            Ожидание (ср)
          </div>
          <div className="text-xl font-bold text-slate-700" data-id="element-9745">
            {stats.avgWait}
          </div>
        </Card>
        <Card padding="sm" className="bg-slate-50 border-slate-200 shadow-sm flex flex-col justify-center" data-id="element-9746">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1" data-id="element-9747">
            Генерация (ср)
          </div>
          <div className="text-xl font-bold text-slate-700" data-id="element-9748">{stats.avgGen}</div>
        </Card>
      </div>

      {/* 2. GLOBAL ACTIONS & FILTERS */}
      <Card padding="md" className="shadow-sm border-slate-200" data-id="element-9749">
        <div className="flex flex-col lg:flex-row justify-between gap-4 mb-4" data-id="element-9750">
          <div className="flex flex-wrap items-center gap-2" data-id="element-9751">
            <Button variant={isPaused ? 'primary' : 'outline'} size="sm" icon={isPaused ? <Play className="w-4 h-4" data-id="element-9753" /> : <Pause className="w-4 h-4" data-id="element-9754" />} onClick={() => handleGlobalAction(isPaused ? 'resume' : 'pause')} className={isPaused ? 'bg-emerald-600 hover:bg-emerald-700 border-none' : ''} data-id="element-9752">
              {isPaused ? 'Запустить очередь' : 'Пауза очереди'}
            </Button>
            <Button variant="outline" size="sm" icon={<RotateCcw className="w-4 h-4" data-id="element-9756" />} onClick={() => handleGlobalAction('retry_failed')} data-id="element-9755">
              Повторить ошибки ({stats.failed})
            </Button>
            <Button variant="outline" size="sm" icon={<Trash2 className="w-4 h-4" data-id="element-9758" />} onClick={() => handleGlobalAction('clear_stuck')} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200" data-id="element-9757">
              Очистить зависшие ({stats.stuck})
            </Button>
            <Button variant="ghost" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-9760" />} onClick={() => handleGlobalAction('refresh')} data-id="element-9759">
              Обновить
            </Button>
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto" data-id="element-9761">
            <div className="relative w-full lg:w-64" data-id="element-9762">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9763" />
              <input type="text" placeholder="Поиск по ID или имени..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9764" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-9766" />} onClick={() => setShowFilters(!showFilters)} data-id="element-9765">
              Фильтры
            </Button>
          </div>
        </div>

        {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-slate-100 animate-in slide-in-from-top-2" data-id="element-9767">
            <div data-id="element-9768">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-9769">
                Статус
              </label>
              <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value
          })} data-id="element-9770">
                <option value="all" data-id="element-9771">Все статусы</option>
                <option value="queued" data-id="element-9772">В очереди</option>
                <option value="processing" data-id="element-9773">Генерация</option>
                <option value="completed" data-id="element-9774">Готово</option>
                <option value="failed" data-id="element-9775">Ошибка</option>
                <option value="stuck" data-id="element-9776">Зависло</option>
              </select>
            </div>
            <div data-id="element-9777">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-9778">
                Приоритет
              </label>
              <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.priority} onChange={e => setFilters({
            ...filters,
            priority: e.target.value
          })} data-id="element-9779">
                <option value="all" data-id="element-9780">Все</option>
                <option value="high" data-id="element-9781">Высокий</option>
                <option value="medium" data-id="element-9782">Средний</option>
                <option value="low" data-id="element-9783">Низкий</option>
              </select>
            </div>
            <div data-id="element-9784">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-9785">
                Источник
              </label>
              <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.source} onChange={e => setFilters({
            ...filters,
            source: e.target.value
          })} data-id="element-9786">
                <option value="all" data-id="element-9787">Все</option>
                <option value="user" data-id="element-9788">Пользователь</option>
                <option value="astrologer" data-id="element-9789">Астролог</option>
                <option value="system" data-id="element-9790">Система</option>
              </select>
            </div>
            <div data-id="element-9791">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-9792">
                Тип отчёта
              </label>
              <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.type} onChange={e => setFilters({
            ...filters,
            type: e.target.value
          })} data-id="element-9793">
                <option value="all" data-id="element-9794">Все типы</option>
                <option value="Натальная карта" data-id="element-9795">Натальная карта</option>
                <option value="Совместимость" data-id="element-9796">Совместимость</option>
                <option value="Соляр" data-id="element-9797">Соляр</option>
                <option value="Прогноз" data-id="element-9798">Прогноз</option>
              </select>
            </div>
            <div data-id="element-9799">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-9800">
                PDF
              </label>
              <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" value={filters.hasPdf} onChange={e => setFilters({
            ...filters,
            hasPdf: e.target.value
          })} data-id="element-9801">
                <option value="all" data-id="element-9802">Все</option>
                <option value="yes" data-id="element-9803">Есть PDF</option>
                <option value="no" data-id="element-9804">Нет PDF</option>
              </select>
            </div>
          </div>}
      </Card>

      {/* 3. MAIN TABLE */}
      <Card padding="none" className="shadow-sm border-slate-200 overflow-visible" data-id="element-9805">
        <div className="overflow-x-auto" data-id="element-9806">
          <table className="w-full text-left whitespace-nowrap" data-id="element-9807">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-9808">
              <tr data-id="element-9809">
                <th className="px-4 py-3 font-medium" data-id="element-9810">ID / Приоритет</th>
                <th className="px-4 py-3 font-medium" data-id="element-9811">
                  Пользователь / Источник
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-9812">Тип / Модель</th>
                <th className="px-4 py-3 font-medium" data-id="element-9813">Статус / Позиция</th>
                <th className="px-4 py-3 font-medium" data-id="element-9814">Тайминг</th>
                <th className="px-4 py-3 font-medium" data-id="element-9815">PDF / Cost</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-9816">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-9817">
              {paginatedQueue.map(item => {
              const PriorityIcon = PRIORITY_CONFIG[item.priority].icon;
              return <tr key={item.id} className="hover:bg-slate-50 transition-colors group" data-id="element-9818">
                    <td className="px-4 py-3" data-id="element-9819">
                      <div className="flex flex-col gap-1" data-id="element-9820">
                        <span className="text-xs font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => handleRowAction('view', item)} data-id="element-9821">
                          {item.id}
                        </span>
                        <div className={`flex items-center gap-1 text-[10px] font-medium ${PRIORITY_CONFIG[item.priority].color}`} data-id="element-9822">
                          <PriorityIcon className="w-3 h-3" data-id="element-9823" />
                          {PRIORITY_CONFIG[item.priority].label}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9824">
                      <div className="flex flex-col gap-1" data-id="element-9825">
                        <span className="text-sm font-medium text-slate-900" data-id="element-9826">
                          {item.userName}
                        </span>
                        <span className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-9827">
                          {item.source === 'astrologer' ? <Star className="w-3 h-3 text-amber-500" data-id="element-9828" /> : item.source === 'system' ? <Settings className="w-3 h-3" data-id="element-9829" /> : <User className="w-3 h-3" data-id="element-9830" />}
                          {item.source === 'astrologer' ? 'Астролог' : item.source === 'system' ? 'Система' : 'Пользователь'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9831">
                      <div className="flex flex-col gap-1 items-start" data-id="element-9832">
                        <span className="text-xs text-slate-700" data-id="element-9833">
                          {item.type}
                        </span>
                        <Badge variant="outline" size="sm" className="text-[9px] bg-white" data-id="element-9834">
                          {item.model}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9835">
                      <div className="flex flex-col gap-1 items-start" data-id="element-9836">
                        <Badge variant={STATUS_CONFIG[item.status]?.variant} size="sm" className="text-[10px]" data-id="element-9837">
                          {STATUS_CONFIG[item.status]?.label}
                        </Badge>
                        {item.position && <span className="text-[10px] font-medium text-slate-500" data-id="element-9838">
                            Позиция: #{item.position}
                          </span>}
                        {item.retries > 0 && <span className="text-[10px] font-medium text-orange-600" data-id="element-9839">
                            Попытка {item.retries + 1}
                          </span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9840">
                      <div className="flex flex-col gap-1 text-[10px]" data-id="element-9841">
                        <span className="text-slate-500 flex items-center gap-1" data-id="element-9842">
                          <Clock className="w-3 h-3" data-id="element-9843" /> Ожидание:{' '}
                          {item.waitTime}
                        </span>
                        {item.eta && <span className="text-amber-600 font-medium flex items-center gap-1" data-id="element-9844">
                            <Timer className="w-3 h-3" data-id="element-9845" /> ETA: {item.eta}
                          </span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9846">
                      <div className="flex flex-col gap-1 items-start" data-id="element-9847">
                        {item.hasPdf ? <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-emerald-100" onClick={() => handleRowAction('pdf', item)} data-id="element-9848">
                            <FileDown className="w-3 h-3" data-id="element-9849" /> PDF готов
                          </span> : <span className="flex items-center gap-1 text-[10px] text-slate-400" data-id="element-9850">
                            <FileText className="w-3 h-3" data-id="element-9851" /> Нет PDF
                          </span>}
                        <span className="text-[10px] text-slate-500 flex items-center gap-0.5" data-id="element-9852">
                          <DollarSign className="w-3 h-3" data-id="element-9853" /> {item.cost}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right relative" data-id="element-9854">
                      <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-slate-900" icon={<MoreVertical className="w-4 h-4" data-id="element-9856" />} onClick={e => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === item.id ? null : item.id);
                  }} data-id="element-9855" />
                      {openMenuId === item.id && <div className="absolute right-8 top-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" data-id="element-9857">
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('view', item, e)} data-id="element-9858">
                            <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-9859" />{' '}
                            Открыть отчёт
                          </button>
                          {item.hasPdf && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('pdf', item, e)} data-id="element-9860">
                              <FileDown className="w-3.5 h-3.5 text-emerald-500" data-id="element-9861" />{' '}
                              Скачать PDF
                            </button>}
                          <div className="h-px bg-slate-100 my-1" data-id="element-9862" />
                          {item.priority !== 'high' && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('priority_up', item, e)} data-id="element-9863">
                              <ChevronUp className="w-3.5 h-3.5 text-blue-500" data-id="element-9864" />{' '}
                              Повысить приоритет
                            </button>}
                          {item.priority !== 'low' && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('priority_down', item, e)} data-id="element-9865">
                              <ChevronDown className="w-3.5 h-3.5 text-slate-500" data-id="element-9866" />{' '}
                              Понизить приоритет
                            </button>}
                          <div className="h-px bg-slate-100 my-1" data-id="element-9867" />
                          {item.status === 'queued' && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('run', item, e)} data-id="element-9868">
                              <Play className="w-3.5 h-3.5 text-emerald-500" data-id="element-9869" />{' '}
                              Запустить сейчас
                            </button>}
                          {['failed', 'stuck', 'canceled'].includes(item.status) && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleRowAction('retry', item, e)} data-id="element-9870">
                              <RotateCcw className="w-3.5 h-3.5 text-amber-500" data-id="element-9871" />{' '}
                              Повторить
                            </button>}
                          {['queued', 'processing', 'retrying'].includes(item.status) && <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => handleRowAction('cancel', item, e)} data-id="element-9872">
                              <XCircle className="w-3.5 h-3.5" data-id="element-9873" /> Отменить
                            </button>}
                        </div>}
                    </td>
                  </tr>;
            })}
              {paginatedQueue.length === 0 && <tr data-id="element-9874">
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-id="element-9875">
                    <Database className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-9876" />
                    <p className="text-sm font-medium" data-id="element-9877">Ничего не найдено</p>
                    <p className="text-xs mt-1" data-id="element-9878">
                      Попробуйте изменить фильтры поиска
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {filteredQueue.length > itemsPerPage && <div className="p-4 border-t border-slate-100" data-id="element-9879">
            <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredQueue.length / itemsPerPage)} onPageChange={setCurrentPage} data-id="element-9880" />
          </div>}
      </Card>

      {showDetailModal && renderDetailModal()}
    </div>;
}